import { CameraProps } from "@repo/core/components/core/Camera/types";
import useToast from "@repo/core/components/core/Toast";
import QrScanner from "qr-scanner";
import { useEffect, useRef, useState } from "react";

export const CameraController = ({
  facing,
  onImageCaptured,
  onBarcodeScanned
}: Pick<CameraProps, "facing" | "onImageCaptured" | "onBarcodeScanned">) => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const qrScanner = useRef<QrScanner | null>(null);
  const { error } = useToast();

  const captureImage = () => {
    if (!videoRef.current) return;
    if (!isCameraReady || !videoRef.current.videoWidth) {
      error("Aguarde a câmera carregar para capturar a foto.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL("image/jpeg");
    onImageCaptured?.(imageData);
  };

  useEffect(() => {
    let activeStream: MediaStream | null = null;
    let isMounted = true;

    const getCameraPermission = async () => {
      setIsCameraReady(false);
      setHasPermission(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing === "front" ? "user" : "environment" }
        });
        activeStream = stream;

        if (isMounted && videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        if (isMounted) {
          setHasPermission(true);
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        if (isMounted) {
          setHasPermission(false);
          setIsCameraReady(false);
          error("Erro ao acessar a câmera, verifique as permissões.");
        }
      }
    };

    getCameraPermission();

    return () => {
      isMounted = false;
      setIsCameraReady(false);

      const stream =
        activeStream || (videoRef.current?.srcObject as MediaStream | null);
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current?.srcObject === stream) {
        videoRef.current.srcObject = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  useEffect(() => {
    if (!videoRef.current || !hasPermission) return;

    const setupScanner = async () => {
      if (!videoRef.current) return;

      qrScanner.current = new QrScanner(
        videoRef.current,
        (result) => {
          onBarcodeScanned?.(result as QrScanner.ScanResult);
        },
        {
          highlightCodeOutline: true,
          maxScansPerSecond: 10
        }
      );

      try {
        await qrScanner.current.start();
      } catch (err) {
        console.error("Error starting QR scanner:", err);
        error("Erro ao iniciar o scanner de QR Code, tente novamente.");
      }
    };

    setupScanner();

    return () => {
      qrScanner.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBarcodeScanned, hasPermission]);
  return {
    videoRef,
    hasPermission,
    isCameraReady,
    setIsCameraReady,
    captureImage
  };
};
